import type { GraphQLSingularResponse, Variables } from 'relay-runtime';

import { getAsyncLocalStorage } from '@confluence/async-local-storage/entry-points/getAsyncLocalStorage.ts';
import { RELAY_SSR_HYDRATION_CONTEXT_STORAGE_NAME } from '@confluence/async-local-storage/entry-points/storageNames.ts';
import { expValEquals } from '@confluence/feature-experiments';

export type RelaySSRGraphQLErrorLike = {
	path?: ReadonlyArray<string | number>;
	extensions?: { classification?: string };
};

type GraphQLSingularResponseWithErrors = GraphQLSingularResponse & {
	errors?: ReadonlyArray<RelaySSRGraphQLErrorLike> | null;
};

type RelaySSRHydrationContext = {
	rootErrorFields: string[];
};

function getRelaySSRHydrationStorage() {
	return getAsyncLocalStorage<RelaySSRHydrationContext>(RELAY_SSR_HYDRATION_CONTEXT_STORAGE_NAME);
}

function getActiveRelaySSRHydrationContext(): RelaySSRHydrationContext | undefined {
	return getRelaySSRHydrationStorage()?.getStore();
}

function sanitizeRootErrorFields(context: RelaySSRHydrationContext | undefined): string[] {
	return Array.isArray(context?.rootErrorFields)
		? context.rootErrorFields.filter((field): field is string => typeof field === 'string')
		: [];
}

export function getRelayGraphQLErrors(
	response: GraphQLSingularResponse,
): ReadonlyArray<RelaySSRGraphQLErrorLike> {
	return (response as GraphQLSingularResponseWithErrors).errors ?? [];
}

function getRootErrorFields(errors: ReadonlyArray<RelaySSRGraphQLErrorLike>): string[] {
	return errors
		.map((error) => error.path)
		.filter((path): path is readonly [string | number] => Array.isArray(path) && path.length === 1)
		.map(([field]) => String(field));
}

function hasValidationErrors(errors: ReadonlyArray<RelaySSRGraphQLErrorLike>): boolean {
	return errors.some((error) => error.extensions?.classification === 'ValidationError');
}

/**
 * Applies the Apollo-compatible Relay SSR hydration policy to a raw GraphQL response. Runtime
 * rendering can still receive the full response, but hydration caches only receive data-only
 * payloads that can be safely replayed without losing required GraphQL error information.
 */
export function getRelaySSRHydrationPolicy(response: GraphQLSingularResponse): {
	cacheableResponse: GraphQLSingularResponse | null;
	rootErrorFields: string[];
	hasValidationErrors: boolean;
} {
	const errors = getRelayGraphQLErrors(response);
	const rootErrorFields = getRootErrorFields(errors);
	const responseHasValidationErrors = hasValidationErrors(errors);

	if (!response.data || rootErrorFields.length > 0 || responseHasValidationErrors) {
		return {
			cacheableResponse: null,
			rootErrorFields,
			hasValidationErrors: responseHasValidationErrors,
		};
	}

	return {
		cacheableResponse: { data: response.data },
		rootErrorFields,
		hasValidationErrors: responseHasValidationErrors,
	};
}

export function getCacheableRelaySSRResponse(
	response: GraphQLSingularResponse,
	operationName?: string,
	variables?: Variables,
): GraphQLSingularResponse | null {
	const policy = getRelaySSRHydrationPolicy(response);
	if (policy.cacheableResponse) {
		return policy.cacheableResponse;
	}

	// The document contract moves optional services into the page-body request. Dropping that
	// entire response after the shell has streamed leaves hydration without the body SSR rendered.
	// Replay the original errors too, so client preloaders retain their error-policy behavior.
	if (
		!policy.hasValidationErrors &&
		policy.rootErrorFields.length > 0 &&
		(operationName === 'ContentUnifiedQuery' || operationName === 'ContentBlogUnifiedQuery') &&
		typeof variables?.contentId === 'string' &&
		variables.contentId.length > 0 &&
		response.data?.content?.nodes?.[0]?.id === variables.contentId &&
		getRelayGraphQLErrors(response).every(
			(error) =>
				error.path?.[0] === 'contentAnalyticsViewers' || error.path?.[0] === 'knowledgeDiscovery',
		) &&
		expValEquals('confluence_exp_vp_document', 'isEnabled', true)
	) {
		return response;
	}

	return null;
}

/**
 * Creates the request-scoped Relay SSR hydration context. The same AsyncLocalStorage store spans
 * Relay network response handling and later __RELAY_STORE__ extraction, which is where root-level
 * GraphQL error fields need to be removed from the serialized hydration store.
 */
export function runWithRelaySSRHydrationContext<T>(callback: () => T): T {
	const storage = getRelaySSRHydrationStorage();
	if (!storage || storage.getStore()) {
		return callback();
	}

	return storage.run({ rootErrorFields: [] }, callback);
}

/**
 * Records root-level GraphQL error fields for the current SSR request. Relay normalizes data without
 * retaining the response-level errors, so store extraction needs this request-scoped context later
 * to remove errored root fields from the serialized hydration store.
 */
export function recordRelaySSRHydrationResult(response: GraphQLSingularResponse): void {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		return;
	}

	const { rootErrorFields } = getRelaySSRHydrationPolicy(response);
	if (rootErrorFields.length === 0) {
		return;
	}

	const context = getActiveRelaySSRHydrationContext();
	if (!context) {
		return;
	}

	context.rootErrorFields = Array.from(
		new Set([...sanitizeRootErrorFields(context), ...rootErrorFields]),
	);
}

export function getRelaySSRHydrationContext(): RelaySSRHydrationContext {
	return {
		rootErrorFields: sanitizeRootErrorFields(getActiveRelaySSRHydrationContext()),
	};
}

export function clearRelaySSRHydrationContext(): void {
	const context = getActiveRelaySSRHydrationContext();
	if (context) {
		context.rootErrorFields = [];
	}
}
